import { checkForSequentialChars, getErrorMessage, ErrorMessageKey, } from '../utils';
import { invalidStrings } from '.';
import { COMMON_PASSWORDS_BLACKLIST } from '..';
/**
 * NOTE: should be using exported constants from `regexMatchers.ts`
 * this causes an obscure defect where the regex check fails
 * and yields the wrong validation result
 */
export const containsNonEnglishCharacters = (str) => {
    const englishLettersLength = (str.match(/[a-z]/gi) || []).length;
    const allLettersLength = (str.match(/\p{L}/gu) || []).length;
    return englishLettersLength !== allLettersLength;
};
export const validateUsername = (username, badWords) => {
    if (!username) {
        return getErrorMessage(ErrorMessageKey.noBlankMsg, 'username');
    }
    // CIAM-12304 - do not allow emojis
    // https://stackoverflow.com/questions/18862256/how-to-detect-emoji-using-javascript
    if (/\p{Extended_Pictographic}/u.test(username)) {
        return getErrorMessage(ErrorMessageKey.noEmoji, 'username');
    }
    if (!/[a-z]/gi.test(username)) {
        return getErrorMessage(ErrorMessageKey.noLettersMsg, 'username');
    }
    if (/\s/g.test(username)) {
        return getErrorMessage(ErrorMessageKey.noSpacesMsg, 'username');
    }
    if (containsNonEnglishCharacters(username) ||
        /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g.test(username) ||
        /[~@#&^()[\]\\|/:;"<>]/g.test(username)) {
        return getErrorMessage(ErrorMessageKey.noSpecialCharactersMsg, 'username');
    }
    if (username.length < 4) {
        return getErrorMessage(ErrorMessageKey.tooShortFourMsg, 'username');
    }
    if (username.length > 28) {
        return getErrorMessage(ErrorMessageKey.tooLongTwentyEightMsg, 'username');
    }
    if (!invalidStrings.validator(badWords)(username)) {
        return invalidStrings.errorMessage;
    }
    // NOTE: wip, waiting for access to BE username check
    // if () {
    //   return getErrorMessage(ErrorMessageKey.usernameTakenMsg, 'username')
    // }
};
export const validateUsernameV2 = (username, badWords) => {
    if (!username) {
        return getErrorMessage(ErrorMessageKey.noBlankMsg, 'username');
    }
    // CIAM-12304 - do not allow emojis
    // https://stackoverflow.com/questions/18862256/how-to-detect-emoji-using-javascript
    if (/\p{Extended_Pictographic}/u.test(username)) {
        return getErrorMessage(ErrorMessageKey.noEmoji, 'username');
    }
    if (!/[a-z]/gi.test(username)) {
        return getErrorMessage(ErrorMessageKey.noLettersMsg, 'username');
    }
    if (username.length < 4) {
        return getErrorMessage(ErrorMessageKey.tooShortFourMsg, 'username');
    }
    if (username.length > 28) {
        return getErrorMessage(ErrorMessageKey.tooLongTwentyEightMsg, 'username');
    }
    if (!invalidStrings.validator(badWords)(username)) {
        return invalidStrings.errorMessage;
    }
    if (/\d{9}/g.test(username)) {
        return getErrorMessage(ErrorMessageKey.unmetRequirementsMatchMsg, 'username');
    }
    if (!/^[a-zA-Z0-9@\-_.]+$/.test(username)) {
        return getErrorMessage(ErrorMessageKey.unmetRequirementsMatchMsg, 'username');
    }
};
export const validatePassword = (password, username) => {
    if (!password) {
        return getErrorMessage(ErrorMessageKey.noBlankMsg, 'password');
    }
    if (!/\d/g.test(password)) {
        return getErrorMessage(ErrorMessageKey.noNumbersMsg, 'password');
    }
    if (!/[a-z]/gi.test(password)) {
        return getErrorMessage(ErrorMessageKey.noLettersMsg, 'password');
    }
    if (/\s/g.test(password)) {
        return getErrorMessage(ErrorMessageKey.noSpacesMsg, 'password');
    }
    if (containsNonEnglishCharacters(password) ||
        /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g.test(password) ||
        /[~@#&^()[\]\\|/:;"<>]/g.test(password)) {
        return getErrorMessage(ErrorMessageKey.noSpecialCharactersMsg, 'password');
    }
    if (password.length < 8) {
        return getErrorMessage(ErrorMessageKey.tooShortEightMsg, 'password');
    }
    if (password.length > 32) {
        return getErrorMessage(ErrorMessageKey.tooLongThirtyTwoMsg, 'password');
    }
    if (password && password.toLowerCase() === username.toLowerCase()) {
        return getErrorMessage(ErrorMessageKey.noPasswordUsernameMatchMsg, 'password');
    }
};
export const validatePasswordV2 = (password, username) => {
    // Order here is important per UX
    if (password.length < 8 || // min length 8
        password.length > 50 || // max length 50
        !/\d/g.test(password) || // at least 1 number
        !/[a-z]/gi.test(password) || // at least 1 letter
        /\s/g.test(password) // cannot contain a space
    ) {
        return getErrorMessage(ErrorMessageKey.unmetPasswordMinRequirementsMatchMsg, 'password');
    }
    if (!/^[a-zA-Z0-9!@#$%^&*()\-?]+$/g.test(password)) {
        return getErrorMessage(ErrorMessageKey.passwordDisallowedCharsMatchMsg, 'password');
    }
    if (COMMON_PASSWORDS_BLACKLIST.some(str => password.toLowerCase().includes(str.toLowerCase()))) {
        return getErrorMessage(ErrorMessageKey.noCommonPasswordsMatchMsg, 'password');
    }
    if (/(.)\1{3,}/g.test(password)) {
        return getErrorMessage(ErrorMessageKey.noRepeatingCharsMatchMsg, 'password');
    }
    if (checkForSequentialChars(password)) {
        return getErrorMessage(ErrorMessageKey.noCharSequenceMatchMsg, 'password');
    }
    if (/\d{9}/g.test(password)) {
        return getErrorMessage(ErrorMessageKey.passwordNoNumberPatternMatchMsg, 'password');
    }
    if (password.toLowerCase().indexOf(username.toLowerCase()) !== -1) {
        return getErrorMessage(ErrorMessageKey.noUsernameInPasswordMatchMsg, 'password');
    }
    return undefined;
};
const passwordLengthRangeValidation = (passwordInput) => passwordInput.length >= 8 &&
    passwordInput.length <= 32 &&
    !/\s/g.test(passwordInput);
const atLeastOneNumber = (passwordInput) => /\d/g.test(passwordInput);
const atLeastOneLetter = (passwordInput) => /[a-z]/gi.test(passwordInput);
export const passwordDirections = [
    {
        text: 'At least 1 number',
        isValid: atLeastOneNumber,
    },
    {
        text: 'At least 1 letter, case sensitive',
        isValid: atLeastOneLetter,
    },
    {
        text: '8 to 32 characters, no spaces',
        isValid: passwordLengthRangeValidation,
    },
];
const passwordV2LengthRangeValidation = (passwordInput) => passwordInput.length >= 8 &&
    passwordInput.length <= 50 &&
    !/\s/g.test(passwordInput);
export const passwordV2Directions = [
    {
        text: '8-50 characters; no spaces, case sensitive',
        isValid: passwordV2LengthRangeValidation,
    },
    {
        text: 'At least one letter',
        isValid: atLeastOneLetter,
    },
    {
        text: 'At least one number',
        isValid: atLeastOneNumber,
    },
];
export const validatePasswordHint = (passwordHint, password, badWords) => {
    if (!passwordHint) {
        return getErrorMessage(ErrorMessageKey.noBlankMsg, 'password hint');
    }
    if (/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g.test(passwordHint) ||
        /\|/g.test(passwordHint) ||
        containsNonEnglishCharacters(passwordHint)) {
        return getErrorMessage(ErrorMessageKey.noSpecialCharactersGrepMsg, 'password hint');
    }
    if (passwordHint.length > 20) {
        return getErrorMessage(ErrorMessageKey.tooLongTwentyMsg, 'password hint');
    }
    if (!invalidStrings.validator(badWords)(passwordHint)) {
        return invalidStrings.errorMessage;
    }
    if (passwordHint &&
        password &&
        passwordHint.toLowerCase().includes(password.toLowerCase())) {
        return getErrorMessage(ErrorMessageKey.noPasswordMatchMsg, 'password hint');
    }
};
export const validateOtp = (otpInput) => {
    if (!otpInput) {
        return 'To continue, please enter your 6-digit security code and select continue.';
    }
    if (otpInput === '25597') {
        return "Please enter a valid security code. The code you entered is the 5-digit 'short code' from the text message. Re-open the text message and enter the 6-digit security code found in the body of the message";
    }
    if (!/^\d+$/.test(otpInput) || otpInput.length !== 6) {
        return 'Please enter a valid, 6-digit security code (numbers only).';
    }
    return undefined;
};
//# sourceMappingURL=formFieldValidation.js.map